import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { fetchChannelConversationCounts } from "@/lib/inbox-stats";
import { reassignConversationsToIntegration } from "@/lib/conversation-integration-assign";

export interface ReconcileIntegrationOption {
  id: string;
  label: string;
  detail?: string;
  status?: string | null;
  /** Indica se esta opção pode ser destino (active). Default true. */
  canBeTarget?: boolean;
  /** Indica se é uma conexão legada (será marcada por padrão como origem). */
  isLegacy?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  channel: "whatsapp" | "instagram" | "messenger";
  channelLabel: string;
  integrations: ReconcileIntegrationOption[];
  defaultIntegrationId?: string | null;
  onDone?: () => void;
}

export function ReconcileOrphanConversationsDialog({
  open,
  onOpenChange,
  workspaceId,
  channel,
  channelLabel,
  integrations,
  defaultIntegrationId,
  onDone,
}: Props) {
  const { toast } = useToast();
  const [unassignedCount, setUnassignedCount] = useState<number | null>(null);
  const [includeUnassigned, setIncludeUnassigned] = useState(true);

  const targetCandidates = useMemo(
    () => integrations.filter((i) => i.canBeTarget !== false),
    [integrations],
  );
  const [targetId, setTargetId] = useState<string | undefined>(
    defaultIntegrationId || targetCandidates[0]?.id,
  );

  const sourceCandidates = useMemo(
    () => integrations.filter((i) => i.id !== targetId),
    [integrations, targetId],
  );
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const [sourceCounts, setSourceCounts] = useState<Record<string, number>>({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ updated: number; remaining: number } | null>(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    const initialTarget = defaultIntegrationId || targetCandidates[0]?.id;
    setTargetId(initialTarget);
    setIncludeUnassigned(true);
    // pré-seleciona origens legadas/desconectadas
    const preselected = new Set<string>();
    for (const i of integrations) {
      if (i.id === initialTarget) continue;
      if (i.isLegacy || (i.status && i.status !== "active")) preselected.add(i.id);
    }
    setSelectedSources(preselected);
    const ids = integrations.map((i) => i.id);
    void fetchChannelConversationCounts(supabase, {
      workspaceId,
      channel,
      integrationIds: ids,
    }).then((counts) => {
      setUnassignedCount(counts.unassigned);
      setSourceCounts(counts.byIntegrationId);
    });
  }, [open, workspaceId, channel, defaultIntegrationId, integrations, targetCandidates]);

  const totalToMove =
    (includeUnassigned ? unassignedCount ?? 0 : 0) +
    Array.from(selectedSources).reduce((sum, id) => sum + (sourceCounts[id] ?? 0), 0);

  const toggleSource = (id: string) => {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReassign = async () => {
    if (!targetId) return;
    setRunning(true);
    setProgress({ updated: 0, remaining: totalToMove });
    try {
      const updated = await reassignConversationsToIntegration(workspaceId, channel, targetId, {
        includeUnassigned,
        sourceIntegrationIds: Array.from(selectedSources),
        onProgress: setProgress,
      });
      toast({
        title: "Conversas reconciliadas",
        description: `${updated} conversa${updated === 1 ? "" : "s"} atribuída${updated === 1 ? "" : "s"} à conexão selecionada.`,
      });
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao reconciliar",
        description: err instanceof Error ? err.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reconciliar conversas</DialogTitle>
          <DialogDescription>
            Atribua conversas {channelLabel} em aberto e/ou de conexões antigas à conexão desejada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Atribuir para
            </label>
            <Select value={targetId} onValueChange={setTargetId} disabled={running}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma conexão" />
              </SelectTrigger>
              <SelectContent>
                {targetCandidates.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.label}
                    {i.detail ? ` · ${i.detail}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">
              Origens a migrar
            </label>
            <div className="rounded-md border divide-y">
              <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40">
                <Checkbox
                  checked={includeUnassigned}
                  onCheckedChange={(c) => setIncludeUnassigned(c === true)}
                  disabled={running}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">Em aberto (sem conexão fixa)</p>
                  <p className="text-xs text-muted-foreground">
                    {unassignedCount === null ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        contando…
                      </span>
                    ) : (
                      `${unassignedCount} conversa${unassignedCount === 1 ? "" : "s"}`
                    )}
                  </p>
                </div>
              </label>
              {sourceCandidates.map((i) => {
                const count = sourceCounts[i.id];
                return (
                  <label
                    key={i.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40"
                  >
                    <Checkbox
                      checked={selectedSources.has(i.id)}
                      onCheckedChange={() => toggleSource(i.id)}
                      disabled={running}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">
                        {i.label}
                        {i.isLegacy ? (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            Legado
                          </span>
                        ) : null}
                        {i.status && i.status !== "active" ? (
                          <span className="ml-1.5 text-[10px] text-muted-foreground">
                            ({i.status === "disconnected" ? "Desconectado" : i.status})
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {count === undefined ? "—" : `${count} conversa${count === 1 ? "" : "s"}`}
                        {i.detail ? ` · ${i.detail}` : ""}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-md border border-yellow-300/50 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 text-xs text-yellow-800 dark:text-yellow-200">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Confira se a conexão destino é o mesmo número/conta das origens selecionadas. Esta ação reatribui <strong>{totalToMove}</strong> conversa{totalToMove === 1 ? "" : "s"}.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>
            Cancelar
          </Button>
          <Button
            onClick={handleReassign}
            disabled={
              running ||
              !targetId ||
              totalToMove === 0 ||
              (!includeUnassigned && selectedSources.size === 0)
            }
          >
            {running ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                {progress
                  ? `Reatribuindo ${progress.updated}${progress.remaining > 0 ? ` de ${progress.updated + progress.remaining}` : ""}...`
                  : "Reatribuindo..."}
              </>
            ) : (
              "Reatribuir conversas"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
